/**
 * Display helpers for the Purchases pages: what the player paid in the Play
 * Store (not the catalogue INR price), the refund time, and what a refund
 * took back from the player.
 */

const DASH = '—';

/**
 * Store price from `storePriceMicros` (a bigint sent as a string, millionths of the
 * currency unit) and `storeCurrency`. A dash when either is missing or the price is not
 * plain digits (whitespace, signs and decimals included). Prices are far below 2^53 micros, so `Number` is exact enough for display.
 */
export function formatStorePrice(
  micros: string | null | undefined,
  currency: string | null | undefined
): string {
  if (!micros || !currency || !/^\d+$/.test(micros)) return DASH;
  const amount = Number(micros) / 1e6;
  if (!Number.isFinite(amount)) return DASH;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

const IST_FORMAT = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * `refundedAt` in IST, e.g. "20 Oct 2026, 10:30 AM IST". The browser's own zone plays no
 * part (the Date column elsewhere is browser-local; refund times are quoted in IST).
 */
export function formatIstRefundTime(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return DASH;
  return `${IST_FORMAT.format(date).replace(/\b(am|pm)\b/i, (m) => m.toUpperCase())} IST`;
}

/** Mirrors the backend `RevokedItemsRecord` (src/modules/purchases/revoked-items.ts). */
export interface RevokedItems {
  source: 'rtdn' | 'voided_api' | 'admin';
  at: string;
  currencies: Record<string, { granted: number; deducted: number; error?: string }>;
  cardsKept: number;
  cardsRemoved?: number;
}

const count = (n: number) => n.toLocaleString('en-US');
const cards = (n: number) => `${n} ${n === 1 ? 'card' : 'cards'}`;

/**
 * One line saying what a refund took back from the player.
 *
 * - Not refunded: a dash.
 * - Refunded with no record: no clawback was recorded (flag off, or a refund from before the
 *   flag existed). An admin refund with "keep items" (`keptByAdmin`) is said plainly.
 * - Otherwise each currency as "deducted of granted", failed ones with their error, then the cards.
 */
export function summarizeRevokedItems(
  r: RevokedItems | null | undefined,
  status: string,
  options: { keptByAdmin?: boolean } = {}
): string {
  if (status !== 'refunded') return DASH;
  if (!r) {
    return options.keptByAdmin ? 'Not taken back' : 'Not taken back (no clawback recorded)';
  }
  const parts = Object.entries(r.currencies ?? {}).map(([name, c]) => {
    const line = `${name} ${count(c.deducted)} of ${count(c.granted)}`;
    return c.error ? `${line} (failed: ${c.error})` : line;
  });
  if (r.cardsRemoved) parts.push(`${cards(r.cardsRemoved)} removed`);
  if (r.cardsKept) parts.push(`${cards(r.cardsKept)} kept`);
  return parts.length ? parts.join('; ') : 'Nothing to take back';
}
